import { useMemo } from 'react';
import { useFormikContext } from 'formik';

import { ErrorField } from '@/components/error-field';
import { RequiredLabel } from '@/components/required-label';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { useUnassociatedAgentsQuery } from '@/query/agent.query';
import { useAssignAgentMutation } from '@/query/phone-number.query';

import type { AssignAgentFormValues } from '@/schemas/phone-number.schema';

import { AssignAgentFormProvider } from '@/formik/phone-number.formik';
import type { PhoneNumberConfiguration } from '@/types/phone-number.type';
import { DialogDescription } from '@radix-ui/react-dialog';

type Props = {
  toggle: () => void;
  phoneNumber: PhoneNumberConfiguration;
};

const SubmitButton = ({ toggle, isPending }: { toggle: () => void; isPending: boolean }) => {
  const { isValid, submitForm } = useFormikContext<AssignAgentFormValues>();

  return (
    <DialogFooter className="mt-4">
      <Button type="button" variant="outline" onClick={toggle}>
        Cancel
      </Button>
      <Button type="button" onClick={submitForm} disabled={!isValid || isPending}>
        {isPending ? 'Updating Phone number...' : 'Save changes'}
      </Button>
    </DialogFooter>
  );
};

const Form = ({ phoneNumber }: { phoneNumber: PhoneNumberConfiguration }) => {
  const { values, touched, errors, setFieldValue } = useFormikContext<AssignAgentFormValues>();
  const { data, isLoading } = useUnassociatedAgentsQuery();

  const agents = data || [];

  const mergedAgents = useMemo(() => {
    const list = [
      ...agents,
      phoneNumber.agent
        ? { label: phoneNumber.agent.agent_name, value: phoneNumber.agent.id.toString() }
        : null,
    ].filter(Boolean);

    return list.filter(
      (agent, index, self) => self.findIndex(a => a.value === agent.value) === index
    );
  }, [agents, phoneNumber.agent]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2">
        <RequiredLabel htmlFor="agent">Agent</RequiredLabel>

        <Select
          onValueChange={value => setFieldValue('agent_id', parseInt(value))}
          value={values.agent_id?.toString() || undefined}
          disabled={isLoading}
        >
          <SelectTrigger
            className={`w-full ${errors.agent_id && touched.agent_id ? 'border-destructive' : ''}`}
            aria-invalid={!!(errors.agent_id && touched.agent_id)}
          >
            <SelectValue placeholder="Select an agent" />
          </SelectTrigger>
          <SelectContent>
            {mergedAgents.map(agent => {
              return (
                <SelectItem key={agent.value} value={agent.value}>
                  {agent.label}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        <ErrorField name="agent_id" />
      </div>
    </div>
  );
};

export const UpdatePhoneNumber = ({ toggle, phoneNumber }: Props) => {
  const mutation = useAssignAgentMutation(phoneNumber.id, {
    onSuccess: () => {
      toggle();
    },
  });

  return (
    <AssignAgentFormProvider
      onSubmit={values => {
        mutation.mutate({ data: values });
      }}
      initialValues={phoneNumber}
    >
      <Dialog open={true} onOpenChange={toggle}>
        <DialogContent>
          <DialogHeader className="mb-4">
            <DialogTitle>Assign Agent</DialogTitle>
            <DialogDescription>Select agent to associate with this phone number.</DialogDescription>
          </DialogHeader>

          {/* Update Form */}
          <Form phoneNumber={phoneNumber} />

          <SubmitButton toggle={toggle} isPending={mutation.isPending} />
        </DialogContent>
      </Dialog>
    </AssignAgentFormProvider>
  );
};
